import { useEffect, useRef, useState } from 'react';
import { useLoad, type Loaded } from './useLoad';

type Call<T> = () => Promise<{ data?: T; error?: unknown; response: Response }>;

export interface Polled<T> {
  /** The last answer; a failed refresh keeps the previous data (see `failed`). */
  result: Loaded<T>;
  /** When the data on screen was fetched (ms), or null. */
  fetchedAt: number | null;
  /** The last refresh failed; the data shown is from `fetchedAt`. */
  failed: boolean;
  reload: () => void;
}

/**
 * `useLoad`, again every `everyMs` while the page is visible (P-004: the Fleet page polls every
 * 5 s). A hidden tab stops polling and refreshes once when it is shown again.
 */
export function usePoll<T>(call: Call<T>, key: string, everyMs: number): Polled<T> {
  const [result, reload] = useLoad(call, key);
  const last = useRef<{ key: string; data: Loaded<T> } | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);

  useEffect(() => {
    if (result.state === 'ok') {
      last.current = { key, data: result };
      setFetchedAt(Date.now());
    }
  }, [result, key]);

  useEffect(() => {
    let timer: number | undefined;
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const start = () => {
      if (timer === undefined && visible()) timer = window.setInterval(reload, everyMs);
    };
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
    };
    const onChange = () => {
      if (visible()) {
        reload();
        start();
      } else stop();
    };
    start();
    document.addEventListener('visibilitychange', onChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onChange);
    };
  }, [key, everyMs, reload]);

  const kept = last.current?.key === key ? last.current.data : null;
  if (result.state === 'error' && kept && result.status !== 401 && result.status !== 403 && result.status !== 404) {
    return { result: kept, fetchedAt, failed: true, reload };
  }
  return { result, fetchedAt, failed: false, reload };
}

/** Re-render every `everyMs` (for "updated 3 s ago"). */
export function useNow(everyMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(t);
  }, [everyMs]);
  return now;
}
